import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaFileExcel, FaUpload, FaDownload, FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaInfoCircle, FaSpinner, FaRedo, FaBook } from 'react-icons/fa';
import { downloadTemplate, parseImportFile, validateImportRows, commitImport, lookupDoi } from '../services/bulkImportService';
import { getUserProfile } from '../services/authService';
import ImportRowsTable from '../components/BulkImport/ImportRowsTable';
import '../styles/submission/bulk-import.css';

const STATUS_META = {
    OK: { label: 'Ready', className: 'ok' },
    WARNING: { label: 'Check', className: 'warning' },
    INCOMPLETE: { label: 'Needs update', className: 'incomplete' },
    ERROR: { label: 'Error', className: 'error' },
};

const DOI_PATTERN = /^10\.\d{4,9}\/\S+$/;

/** The Solutions sheet rows as the server expects them back with validate and commit. */
const solutionInputs = (report) => (report?.solutions || []).map(s => ({ rowNumber: s.rowNumber, values: s.values }));

/** The Solutions sheet: each solution (or part of a mixed one), the runs using it and its problems. Read-only. */
function SolutionsSheetPanel({ solutions }) {
    const [open, setOpen] = useState(() => solutions.some(s => s.issues.some(i => i.severity === 'ERROR' || i.severity === 'MISSING')));
    const problems = solutions.reduce((n, s) => n + s.issues.filter(i => i.severity !== 'INFO').length, 0);
    return (
        <div className="bulk-solutions mb-3 border rounded p-3 bg-light-subtle">
            <button type="button" className="btn btn-link p-0 fw-bold text-decoration-none" onClick={() => setOpen(o => !o)} aria-expanded={open}>
                {open ? '▾' : '▸'} Solutions sheet: {new Set(solutions.map(s => s.solutionId)).size} solution(s) in {solutions.length} row(s)
                {problems > 0 && <span className="text-danger ms-2">{problems} to review</span>}
            </button>
            {open && (
                <>
                    <p className="small text-muted mt-2 mb-2">
                        Runs refer to these by <code>solution</code>, <code>core_solution</code> or <code>shell_solution</code>.
                        Problems in a solution are also shown on the runs using it. To change a solution, edit the Solutions sheet and upload the file again.
                    </p>
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                                <tr><th scope="col">Row</th><th scope="col">Id</th><th scope="col">Solution</th><th scope="col">Used by rows</th><th scope="col">Problems</th></tr>
                            </thead>
                            <tbody>
                                {solutions.map(s => (
                                    <tr key={s.rowNumber}>
                                        <td>{s.rowNumber}</td>
                                        <td><strong>{s.solutionId || '—'}</strong></td>
                                        <td className="small">{s.summary || '—'}</td>
                                        <td className="small">{s.usedBy.length ? s.usedBy.join(', ') : <span className="text-muted">none</span>}</td>
                                        <td className="small">
                                            {s.issues.filter(i => i.severity !== 'INFO').length === 0
                                                ? <span className="text-muted">none</span>
                                                : <ul className="mb-0 ps-3">{s.issues.filter(i => i.severity !== 'INFO').map((i, k) => (
                                                    <li key={k} className={i.severity === 'ERROR' ? 'text-danger' : ''}>
                                                        {i.column && <code className="me-1">{i.column}</code>}{i.message}
                                                    </li>
                                                ))}</ul>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

export default function BulkImportPage() {
    const [phase, setPhase] = useState('upload'); // upload | review | done
    const [busy, setBusy] = useState(null); // null | 'parse' | 'validate' | 'commit' | 'template'
    const [error, setError] = useState(null);
    const [fileName, setFileName] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null); // sent with the commit for traceability
    const [report, setReport] = useState(null);
    const [excluded, setExcluded] = useState(() => new Set());
    const [proposals, setProposals] = useState([]); // new polymer/solvent names: { kind, name, fullName, casNumber, note }
    const [paperMeta, setPaperMeta] = useState({}); // key -> { title, abstract, source }
    const [filter, setFilter] = useState('ALL');
    const [consent, setConsent] = useState(false);
    const [profileName, setProfileName] = useState(null);
    const [contributorName, setContributorName] = useState('');
    const [result, setResult] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const fileInput = useRef(null);

    useEffect(() => {
        getUserProfile().then(p => setProfileName(p?.name || '')).catch(() => setProfileName(''));
    }, []);

    // Look up titles for publications whose rows give a DOI but no title.
    useEffect(() => {
        if (!report) return;
        report.papers.forEach(paper => {
            if (!paper.key || paper.publicationTitle || paperMeta[paper.key] || !DOI_PATTERN.test(paper.key)) return;
            setPaperMeta(prev => ({ ...prev, [paper.key]: { source: 'lookup' } }));
            lookupDoi(paper.key).then(found => {
                setPaperMeta(prev => ({
                    ...prev,
                    [paper.key]: found ? { title: found.title, abstract: found.abstract, source: 'crossref' } : { source: 'notfound' },
                }));
            });
        });
    }, [report]);

    const handleTemplate = async () => {
        setBusy('template');
        setError(null);
        try {
            await downloadTemplate();
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(null);
        }
    };

    const handleFile = async (file) => {
        if (!file) return;
        setBusy('parse');
        setError(null);
        try {
            const parsed = await parseImportFile(file);
            setFileName(file.name);
            setUploadedFile(file);
            setReport(parsed);
            setExcluded(new Set());
            setProposals([]);
            setPaperMeta({});
            setFilter('ALL');
            setPhase('review');
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(null);
            if (fileInput.current) fileInput.current.value = '';
        }
    };

    /** Re-checks all rows after an edit; the server returns the normalised values and new issues. */
    const revalidate = async (rows, withProposals = proposals) => {
        setBusy('validate');
        setError(null);
        try {
            const checked = await validateImportRows(rows.map(r => ({ rowNumber: r.rowNumber, values: r.values })), withProposals,
                solutionInputs(report));
            // Re-checks don't recount existing records per DOI; keep the counts from the upload.
            setReport(prev => ({
                ...prev,
                rows: checked.rows,
                solutions: checked.solutions || prev.solutions,
                papers: checked.papers.map(p => ({
                    ...p,
                    existingRecords: p.existingRecords ?? prev.papers.find(q => q.key === p.key)?.existingRecords ?? 0,
                })),
            }));
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(null);
        }
    };

    /** Sets a cell, in this row or - with applyToAll - in every row holding the same value. */
    const handleEdit = (rowNumber, column, value, applyToAll = false) => {
        const original = report.rows.find(r => r.rowNumber === rowNumber)?.values[column];
        const rows = report.rows.map(r => {
            const matches = r.rowNumber === rowNumber || (applyToAll && (r.values[column] || '') === (original || ''));
            if (!matches) return r;
            const values = { ...r.values };
            if (value === '' || value == null) delete values[column];
            else values[column] = value;
            return { ...r, values };
        });
        revalidate(rows);
    };

    /** Proposes a new polymer/solvent name; rows using it become "Needs update" instead of errors. */
    const handlePropose = (proposal) => {
        const next = [...proposals.filter(p => !(p.kind === proposal.kind && p.name.toLowerCase() === proposal.name.toLowerCase())), proposal];
        setProposals(next);
        revalidate(report.rows, next);
    };

    const handleWithdraw = (kind, name) => {
        const next = proposals.filter(p => !(p.kind === kind && p.name.toLowerCase() === name.toLowerCase()));
        setProposals(next);
        revalidate(report.rows, next);
    };

    const toggleExcluded = (rowNumber) => {
        setExcluded(prev => {
            const next = new Set(prev);
            if (next.has(rowNumber)) next.delete(rowNumber);
            else next.add(rowNumber);
            return next;
        });
    };

    const included = useMemo(
        () => (report ? report.rows.filter(r => !excluded.has(r.rowNumber)) : []),
        [report, excluded]);
    const submittable = included.filter(r => r.status !== 'ERROR');
    const counts = useMemo(() => {
        const c = { ALL: report?.rows.length || 0, OK: 0, WARNING: 0, INCOMPLETE: 0, ERROR: 0 };
        (report?.rows || []).forEach(r => { c[r.status] += 1; });
        return c;
    }, [report]);
    const submittableIncomplete = submittable.filter(r => r.status === 'INCOMPLETE').length;
    const submittablePapers = new Set(submittable.map(r => r.paperKey)).size;
    const needName = profileName === '';
    const fileBlocked = (report?.fileIssues || []).some(i => i.severity === 'ERROR') && submittable.length === 0;

    const handleCommit = async () => {
        setBusy('commit');
        setError(null);
        try {
            const papers = Object.entries(paperMeta)
                .filter(([, meta]) => meta.title || meta.abstract)
                .map(([key, meta]) => ({ key, publicationTitle: meta.title || null, abstractText: meta.abstract || null }));
            const committed = await commitImport({
                rows: submittable.map(r => ({ rowNumber: r.rowNumber, values: r.values })),
                papers,
                consentTerms: consent,
                contributorName: needName ? contributorName : null,
                proposals,
                solutions: solutionInputs(report),
            }, uploadedFile);
            setResult(committed);
            setPhase('done');
            window.scrollTo(0, 0);
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(null);
        }
    };

    const reset = () => {
        setPhase('upload');
        setReport(null);
        setResult(null);
        setError(null);
        setConsent(false);
        setProposals([]);
    };

    return (
        <div className="container py-4 bulk-import">
            <header className="mb-4">
                <h1 className="bulk-title">Bulk upload</h1>
                <p className="text-muted mb-0">
                    Upload many experimental runs at once from a spreadsheet. Every row is checked before anything
                    is stored; rows with missing data can still be submitted and completed later.
                </p>
            </header>

            {error && (
                <div className="alert alert-danger d-flex align-items-start gap-2" role="alert">
                    <FaTimesCircle className="mt-1 flex-shrink-0" />
                    <div>{error}</div>
                </div>
            )}

            {phase === 'upload' && (
                <div className="row g-4">
                    <div className="col-lg-5">
                        <section className="bulk-card h-100">
                            <h2 className="bulk-step"><span>1</span> Fill in the template</h2>
                            <p>
                                One row per experimental run, one column per parameter. Dropdowns list the accepted
                                polymers, solvents and units; the README sheet explains every column.
                            </p>
                            <ul className="bulk-tips">
                                <li>Rows with the same DOI become one contribution.</li>
                                <li>Blank unit cells use the default unit, or type it with the value, e.g. <code>15 kV</code>.</li>
                                <li>Several morphology or instability labels: separate them with <code>;</code></li>
                                <li>CSV files with the same header work too.</li>
                            </ul>
                            <button className="btn btn-outline-success" onClick={handleTemplate} disabled={busy === 'template'}>
                                {busy === 'template' ? <FaSpinner className="spin me-2" /> : <FaDownload className="me-2" />}
                                Download template (.xlsx)
                            </button>
                            <p className="small text-muted mt-3 mb-0">
                                A file exported from the dataset page (older JSON-cell layout) can also be uploaded.
                            </p>
                        </section>
                    </div>
                    <div className="col-lg-7">
                        <section className="bulk-card h-100">
                            <h2 className="bulk-step"><span>2</span> Upload and review</h2>
                            <div
                                className={`bulk-dropzone ${dragOver ? 'over' : ''}`}
                                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
                                onClick={() => fileInput.current?.click()}
                                role="button"
                                tabIndex={0}
                                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') fileInput.current?.click(); }}
                            >
                                {busy === 'parse' ? (
                                    <><FaSpinner className="spin bulk-drop-icon" /><div>Checking your file…</div></>
                                ) : (
                                    <>
                                        <FaFileExcel className="bulk-drop-icon" />
                                        <div className="fw-semibold">Drop an .xlsx or .csv file here</div>
                                        <div className="small text-muted">or click to choose a file</div>
                                    </>
                                )}
                            </div>
                            <input ref={fileInput} type="file" accept=".xlsx,.csv" hidden
                                onChange={e => handleFile(e.target.files[0])} />
                            <p className="small text-muted mt-3 mb-0">
                                Nothing is submitted until you confirm in the next step.
                            </p>
                        </section>
                    </div>
                </div>
            )}

            {phase === 'review' && report && (
                <>
                    <div className="bulk-card mb-4">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                            <div>
                                <div className="fw-semibold"><FaFileExcel className="text-success me-2" />{fileName}</div>
                                <div className="small text-muted">
                                    {counts.ALL} row{counts.ALL === 1 ? '' : 's'} · {report.papers.length} publication{report.papers.length === 1 ? '' : 's'}
                                    {report.format === 'legacy' && ' · converted from the dataset export layout'}
                                </div>
                            </div>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => fileInput.current?.click()} disabled={!!busy}>
                                <FaRedo className="me-2" />Upload a corrected file
                            </button>
                            <input ref={fileInput} type="file" accept=".xlsx,.csv" hidden
                                onChange={e => handleFile(e.target.files[0])} />
                        </div>
                        {(report.fileIssues || []).map((issue, i) => (
                            <div key={i} className={`bulk-file-issue ${issue.severity.toLowerCase()} mt-3`}>
                                {issue.severity === 'ERROR' ? <FaTimesCircle /> : issue.severity === 'WARNING' ? <FaExclamationTriangle /> : <FaInfoCircle />}
                                <span>{issue.message}</span>
                            </div>
                        ))}
                    </div>

                    <section className="mb-4">
                        <h2 className="bulk-section-title"><FaBook className="me-2" />Publications</h2>
                        <p className="small text-muted">Each publication becomes one contribution. Titles are looked up from the DOI when the file has none; you can edit them.</p>
                        <div className="row g-3">
                            {report.papers.map(paper => {
                                const meta = paperMeta[paper.key] || {};
                                const title = meta.title ?? paper.publicationTitle ?? '';
                                return (
                                    <div className="col-md-6" key={paper.key || 'none'}>
                                        <div className="bulk-paper">
                                            <div className="small text-muted text-truncate">
                                                {paper.doi ? `DOI ${paper.doi}` : 'No DOI (unpublished data)'}
                                            </div>
                                            <input
                                                className="form-control form-control-sm mt-1"
                                                value={title}
                                                placeholder={meta.source === 'lookup' ? 'Looking up title…' : 'Publication title'}
                                                onChange={e => setPaperMeta(prev => ({ ...prev, [paper.key]: { ...meta, title: e.target.value, source: 'user' } }))}
                                                aria-label="Publication title"
                                            />
                                            {meta.source === 'crossref' && <div className="small text-muted mt-1">Title from Crossref.</div>}
                                            {meta.source === 'notfound' && <div className="small text-warning mt-1">The DOI was not found on Crossref; please check it.</div>}
                                            <div className="small mt-2">
                                                {paper.rowCount} row{paper.rowCount === 1 ? '' : 's'}
                                                {paper.errorRows > 0 && <span className="text-danger"> · {paper.errorRows} with errors</span>}
                                                {paper.incompleteRows > 0 && <span className="bulk-text-incomplete"> · {paper.incompleteRows} need update</span>}
                                            </div>
                                            {paper.existingRecords > 0 && (
                                                <div className="bulk-file-issue warning mt-2">
                                                    <FaExclamationTriangle />
                                                    <span>The database already has {paper.existingRecords} record{paper.existingRecords === 1 ? '' : 's'} for this DOI. Make sure you are not uploading them again.</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    <section className="mb-4">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                            <h2 className="bulk-section-title mb-0">Rows</h2>
                            <div className="btn-group btn-group-sm flex-wrap" role="group" aria-label="Filter rows by status">
                                {['ALL', 'ERROR', 'INCOMPLETE', 'WARNING', 'OK'].map(key => (
                                    <button key={key} type="button"
                                        className={`btn ${filter === key ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => setFilter(key)}>
                                        {key === 'ALL' ? 'All' : STATUS_META[key].label} ({counts[key]})
                                    </button>
                                ))}
                            </div>
                        </div>
                        {busy === 'validate' && <div className="small text-muted mb-2"><FaSpinner className="spin me-2" />Re-checking…</div>}
                        {report.solutions?.length > 0 && <SolutionsSheetPanel solutions={report.solutions} />}
                        <ImportRowsTable
                            rows={report.rows.filter(r => filter === 'ALL' || r.status === filter)}
                            allRows={report.rows}
                            statusMeta={STATUS_META}
                            excluded={excluded}
                            onToggleExcluded={toggleExcluded}
                            onEdit={handleEdit}
                            onPropose={handlePropose}
                            onWithdraw={handleWithdraw}
                            proposals={proposals}
                            disabled={!!busy}
                        />
                    </section>

                    <section className="bulk-card bulk-submit">
                        <h2 className="bulk-section-title">Submit</h2>
                        <p className="mb-2">
                            <strong>{submittable.length}</strong> row{submittable.length === 1 ? '' : 's'} will be submitted as{' '}
                            <strong>{submittablePapers}</strong> contribution{submittablePapers === 1 ? '' : 's'} for review.
                            {submittableIncomplete > 0 && <> <strong>{submittableIncomplete}</strong> of them will be marked <em>Needs update</em> until you complete them in My Submissions.</>}
                        </p>
                        {proposals.length > 0 && (
                            <p className="small mb-2">
                                New names you propose: {proposals.map(p => <span key={p.kind + p.name} className="bulk-proposal-chip">{p.name} <span className="text-muted">({p.kind.toLowerCase()})</span></span>)}.
                                {' '}Rows using them are saved as <em>Needs update</em> and completed automatically once an admin accepts the name.
                            </p>
                        )}
                        {counts.ERROR > 0 && (
                            <p className="small text-danger mb-2">
                                {included.length - submittable.length} row{included.length - submittable.length === 1 ? ' has' : 's have'} errors and will be left out. Fix them above or in your file.
                            </p>
                        )}
                        {needName && (
                            <div className="mb-3" style={{ maxWidth: 420 }}>
                                <label htmlFor="contributorName" className="form-label small fw-semibold">Your name (shown as contributor)</label>
                                <input id="contributorName" className="form-control" value={contributorName}
                                    onChange={e => setContributorName(e.target.value)} />
                            </div>
                        )}
                        <div className="form-check mb-3">
                            <input className="form-check-input" type="checkbox" id="bulkConsent" checked={consent}
                                onChange={e => setConsent(e.target.checked)} />
                            <label className="form-check-label" htmlFor="bulkConsent">
                                I agree to the <Link to="/terms">Terms of Use</Link> and <Link to="/license">License Agreement</Link>.
                            </label>
                        </div>
                        <button className="btn btn-primary bulk-primary"
                            disabled={!consent || submittable.length === 0 || !!busy || fileBlocked || (needName && !contributorName.trim())}
                            onClick={handleCommit}>
                            {busy === 'commit' ? <FaSpinner className="spin me-2" /> : <FaUpload className="me-2" />}
                            Submit {submittable.length} row{submittable.length === 1 ? '' : 's'}
                        </button>
                        <button className="btn btn-link" onClick={reset} disabled={!!busy}>Start over</button>
                    </section>
                </>
            )}

            {phase === 'done' && result && (
                <section className="bulk-card text-center py-5">
                    <FaCheckCircle className="text-success mb-3" style={{ fontSize: '3rem' }} />
                    <h2 className="bulk-section-title">Thank you, your data was submitted</h2>
                    <p className="text-muted">It will appear in the dataset once a moderator has reviewed it.</p>
                    <div className="table-responsive mx-auto" style={{ maxWidth: 720 }}>
                        <table className="table table-sm text-start align-middle">
                            <thead><tr><th>Publication</th><th className="text-end">Records</th><th className="text-end">Pending review</th><th className="text-end">Needs update</th></tr></thead>
                            <tbody>
                                {result.submissions.map(s => (
                                    <tr key={s.submissionId}>
                                        <td className="text-break">{s.doi || 'No DOI'}</td>
                                        <td className="text-end">{s.records}</td>
                                        <td className="text-end">{s.pending}</td>
                                        <td className="text-end">{s.needsUpdate}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {result.proposedNames?.length > 0 && (
                        <p className="small text-muted">
                            Proposed names waiting for an admin: {result.proposedNames.join(', ')}. Records using them
                            will be completed and sent for review as soon as the names are accepted.
                        </p>
                    )}
                    <div className="d-flex justify-content-center gap-2 mt-3">
                        <Link to="/my-submissions" className="btn btn-primary bulk-primary">Go to My Submissions</Link>
                        <button className="btn btn-outline-secondary" onClick={reset}>Upload another file</button>
                    </div>
                </section>
            )}
        </div>
    );
}
